<script setup lang="ts">
import { AI_PRESENCE_COLOR, useAiLiveStore } from '@/store/aiLiveStore';
import { usePresenceStore, type PresenceUser } from '@/store/presenceStore';
import { useProfileStore } from '@/store/profileStore';
import { useMapStore } from '@/store/remapStore';
import { useUserStore } from '@/store/userStore';

const presenceStore = usePresenceStore();
const profileStore = useProfileStore();
const userStore = useUserStore();
const mapStore = useMapStore();
const aiLiveStore = useAiLiveStore();

// Colleagues are live on project maps only; the AI assistant is on both
// (WI #5767).
const otherUsers = computed(() =>
  mapStore.isTemplate
    ? []
    : presenceStore.users.filter((u) => u.id !== profileStore.profile.id),
);

const aiMode = computed(() => (mapStore.isTemplate ? 'template' : 'project'));

/**
 * "KI-Assistent" is here while an answer of this map runs in this tab — the
 * same state its ring and the follow banner read.
 */
const isAiPresent = computed(
  () => !!mapStore.mapId && aiLiveStore.isActive(aiMode.value, mapStore.mapId),
);

const isFollowingAi = computed(() =>
  aiLiveStore.isFollowing(aiMode.value, mapStore.mapId),
);

function followAi() {
  aiLiveStore.follow(aiMode.value, mapStore.mapId);
}

function unfollowAi() {
  aiLiveStore.stopFollowing(aiMode.value, mapStore.mapId);
}

const hasOverflow = computed(() => otherUsers.value.length > 2);
const visibleUsers = computed(() =>
  hasOverflow.value ? otherUsers.value.slice(0, 1) : otherUsers.value,
);
const overflowUsers = computed(() =>
  hasOverflow.value ? otherUsers.value.slice(1) : [],
);

function getInitials(user: PresenceUser): string {
  const first = user.first_name?.[0] ?? '';
  const last = user.last_name?.[0] ?? '';

  return (first + last).toUpperCase() || '?';
}

function getFullName(user: PresenceUser): string {
  return [user.first_name, user.last_name].filter(Boolean).join(' ') || '—';
}

watch(
  () => presenceStore.users,
  (users) => {
    for (const user of users) {
      userStore.loadUserAvatar(user.id, user.has_profile_picture);
    }
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <div v-if="otherUsers.length > 0 || isAiPresent" class="presence-avatars">
    <UiPopover
      v-if="otherUsers.length > 0"
      placement="bottom"
      hover
      theme="primary"
    >
      <template #default>
        <button
          class="live-cursor-toggle"
          :class="{ active: presenceStore.showLiveCursors }"
          @click="presenceStore.toggleLiveCursors()"
        >
          <UiIcon
            :name="presenceStore.showLiveCursors ? 'Eye' : 'EyeOff'"
            class="toggle-icon"
          />
        </button>
      </template>

      <template #content>
        <span class="user-name-tooltip">
          {{
            presenceStore.showLiveCursors
              ? $t('presence.hideLiveCursors')
              : $t('presence.showLiveCursors')
          }}
        </span>
      </template>
    </UiPopover>

    <div v-if="isAiPresent" class="presence-slot">
      <UiPopover placement="bottom" theme="primary">
        <template #default>
          <div
            class="avatar-item"
            :class="{ 'is-following': isFollowingAi }"
            data-testid="ai-presence-avatar"
          >
            <span
              class="ai-avatar"
              :style="{ backgroundColor: AI_PRESENCE_COLOR }"
            >
              <UiIcon name="Sparkles" class="ai-avatar-icon" />
            </span>
          </div>
        </template>

        <template #content>
          <div class="avatar-popover">
            <span class="user-name-tooltip">{{
              $t('presence.aiAssistant')
            }}</span>
            <button
              v-if="!isFollowingAi"
              class="follow-btn"
              data-testid="ai-presence-follow"
              @click="followAi"
            >
              <UiIcon name="Eye" class="follow-icon" />
              {{ $t('presence.follow') }}
            </button>
            <button
              v-else
              class="follow-btn follow-btn--active"
              data-testid="ai-presence-unfollow"
              @click="unfollowAi"
            >
              <UiIcon name="EyeOff" class="follow-icon" />
              {{ $t('presence.unfollow') }}
            </button>
          </div>
        </template>
      </UiPopover>
    </div>

    <div v-for="user in visibleUsers" :key="user.id" class="presence-slot">
      <UiPopover placement="bottom" theme="primary">
        <template #default>
          <div
            class="avatar-item"
            :class="{
              'is-following': presenceStore.followingUserId === user.id,
              'is-follower': presenceStore.followers.includes(user.id),
            }"
          >
            <UiAvatar
              :initials="getInitials(user)"
              :image-url="userStore.avatarUrls[user.id] ?? ''"
              :width="32"
              :height="32"
              :font-size="13"
            />
            <span
              v-if="presenceStore.followers.includes(user.id)"
              class="follower-badge"
            >
              <UiIcon name="Eye" class="follower-badge-icon" />
            </span>
          </div>
        </template>

        <template #content>
          <div class="avatar-popover">
            <span class="user-name-tooltip">{{ getFullName(user) }}</span>
            <button
              v-if="
                !presenceStore.isFollowing && !presenceStore.isBeingFollowed
              "
              class="follow-btn"
              @click="presenceStore.followUser(user.id)"
            >
              <UiIcon name="Eye" class="follow-icon" />
              {{ $t('presence.follow') }}
            </button>
            <button
              v-else-if="presenceStore.followingUserId === user.id"
              class="follow-btn follow-btn--active"
              @click="presenceStore.unfollowUser()"
            >
              <UiIcon name="EyeOff" class="follow-icon" />
              {{ $t('presence.unfollow') }}
            </button>
          </div>
        </template>
      </UiPopover>
    </div>

    <div v-if="overflowUsers.length > 0" class="presence-slot">
      <UiPopover placement="bottom" theme="primary">
        <template #default>
          <div class="overflow-avatar">
            <span>+{{ overflowUsers.length }}</span>
          </div>
        </template>

        <template #content>
          <div class="overflow-tooltip">
            <div
              v-for="user in overflowUsers"
              :key="user.id"
              class="overflow-tooltip-user"
            >
              <div class="overflow-user-info">
                <UiAvatar
                  :initials="getInitials(user)"
                  :image-url="userStore.avatarUrls[user.id] ?? ''"
                  :width="24"
                  :height="24"
                  :font-size="10"
                />
                <span>{{ getFullName(user) }}</span>
                <UiIcon
                  v-if="presenceStore.followers.includes(user.id)"
                  name="Eye"
                  class="overflow-follower-icon"
                />
              </div>
              <button
                v-if="
                  !presenceStore.isFollowing && !presenceStore.isBeingFollowed
                "
                class="overflow-follow-btn"
                @click="presenceStore.followUser(user.id)"
              >
                <UiIcon name="Eye" class="follow-icon" />
              </button>
              <button
                v-else-if="presenceStore.followingUserId === user.id"
                class="overflow-follow-btn overflow-follow-btn--active"
                @click="presenceStore.unfollowUser()"
              >
                <UiIcon name="EyeOff" class="follow-icon" />
              </button>
            </div>
          </div>
        </template>
      </UiPopover>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.presence-avatars {
  display: flex;
  align-items: center;
  margin-right: var(--space-8);
  position: relative;
  z-index: 10;
}

.presence-slot {
  position: relative;

  // vue3-popper keeps a popover open across the gap with a transparent
  // border around its trigger. Sideways it reached over the next avatar,
  // which this one overlaps, and took its hover: only the gap below counts.
  :deep(.popover) {
    border-left-width: 0 !important;
    border-right-width: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  // First avatar on top, each subsequent one behind the previous
  @for $i from 1 through 4 {
    &:nth-child(#{$i}) {
      z-index: #{5 - $i};
    }
  }
}

.presence-slot + .presence-slot {
  margin-left: px(-8);
}

.live-cursor-toggle {
  width: px(28);
  height: px(28);
  border-radius: var(--border-radius-circle);
  border: none;
  background: var(--color-gray-100);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-right: var(--space-6);
  transition: background var(--duration-base);

  &:hover {
    background: var(--color-gray-200);
  }

  &.active {
    background: var(--color-primary-100);

    .toggle-icon {
      color: var(--color-primary-600);
    }
  }
}

.toggle-icon {
  width: px(14);
  height: px(14);
  color: var(--color-gray-400);
}

.avatar-item {
  cursor: pointer;
  border-radius: var(--border-radius-circle);
  border: px(2) solid transparent;
  transition: border-color var(--duration-base);
  position: relative;

  &.is-following {
    border-color: var(--color-primary-400);
    box-shadow: 0 0 0 px(2) rgba(var(--color-primary-400--rgb), 0.3);
  }

  &.is-follower {
    border-color: var(--color-success-500);
    box-shadow: 0 0 0 px(2) rgba(16, 185, 129, 0.3);
  }
}

.ai-avatar {
  width: px(32);
  height: px(32);
  border-radius: var(--border-radius-circle);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-avatar-icon {
  width: px(16);
  height: px(16);
  color: white;
}

.follower-badge {
  position: absolute;
  bottom: px(-2);
  right: px(-2);
  width: px(16);
  height: px(16);
  border-radius: var(--border-radius-circle);
  background: var(--color-success-500);
  display: flex;
  align-items: center;
  justify-content: center;
  border: px(1) solid var(--color-gray-0);
}

.follower-badge-icon {
  width: px(10);
  height: px(10);
  color: white;
}

.avatar-popover {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-6);
}

.follow-btn {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-8);
  border: none;
  border-radius: var(--border-radius-4);
  background: var(--color-primary-100);
  color: var(--color-primary-700);
  font-size: var(--font-size-12);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background var(--duration-base);
  white-space: nowrap;

  &:hover {
    background: var(--color-primary-200);
  }

  &--active {
    background: var(--color-gray-100);
    color: var(--color-gray-600);

    &:hover {
      background: var(--color-gray-200);
    }
  }
}

.follow-icon {
  width: px(12);
  height: px(12);
}

.overflow-avatar {
  width: px(32);
  height: px(32);
  border-radius: var(--border-radius-circle);
  background: var(--color-primary-100);
  border: px(2) solid var(--color-primary-200);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-11);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-600);
  cursor: default;
}

.user-name-tooltip {
  white-space: nowrap;
}

.overflow-tooltip {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.overflow-tooltip-user {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  white-space: nowrap;
  font-size: var(--font-size-13);
}

.overflow-user-info {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  flex: 1;
}

.overflow-follower-icon {
  width: px(12);
  height: px(12);
  color: var(--color-success-500);
}

.overflow-follow-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: px(24);
  height: px(24);
  border: none;
  border-radius: var(--border-radius-4);
  background: var(--color-primary-100);
  color: var(--color-primary-600);
  cursor: pointer;
  flex-shrink: 0;
  transition: background var(--duration-base);

  &:hover {
    background: var(--color-primary-200);
  }

  &--active {
    background: var(--color-gray-100);
    color: var(--color-gray-500);

    &:hover {
      background: var(--color-gray-200);
    }
  }

  .follow-icon {
    width: px(14);
    height: px(14);
  }
}
</style>
